import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers';
import './style.css';

const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item) return '#';
  const url = item['@id'] || '#';
  const isFile = item['@type'] === 'File';
  return isFile ? `${url}/@@download/file` : url;
};

// ---------------------------------------------------------------------------
// extractTOCEntries — ported from the multi-hero version
// Handles slate/text heading nodes AND block title fields.
// Skips the hero block itself via selfBlockId.
// ---------------------------------------------------------------------------
const extractTOCEntries = (
  blocks,
  layout,
  useH2 = true,
  useH3 = true,
  useBlockTitles = false,
  selfBlockId = null,
) => {
  const entries = [];
  const allowedTypes = [
    ...(useH2 ? ['heading-two', 'h2'] : []),
    ...(useH3 ? ['heading-three', 'h3'] : []),
  ];

  layout.forEach((blockId) => {
    if (blockId === selfBlockId) return;
    const block = blocks[blockId];
    if (!block) return;

    // Slate / text blocks — extract heading nodes
    if (
      (block['@type'] === 'slate' || block['@type'] === 'text') &&
      Array.isArray(block.value)
    ) {
      block.value.forEach((node) => {
        if (allowedTypes.includes(node.type)) {
          const text = node.children?.map((child) => child.text).join('').trim();
          if (text) {
            const anchor = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            entries.push({ id: anchor, title: text });
          }
        }
      });
    }

    // Block title fields (e.g. listing block headline, data-vis heading, etc.)
    if (useBlockTitles) {
      const titleField =
        block.headline || block.title || block.heading || block.headerText;
      if (titleField && typeof titleField === 'string' && titleField.trim()) {
        const text = titleField.trim();
        const anchor = text
          .toLowerCase()
          .replace(/<[^>]*>/g, '')
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
        entries.push({ id: anchor, title: text, isBlockTitle: true, blockId });
      }
    }
  });

  return entries;
};

const View = (props) => {
  const { data, properties = {}, block: selfBlockId } = props;
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoRef, setVideoRef] = useState(null);

  // Breadcrumbs from redux store
  const breadcrumbItems = useSelector((state) => state.breadcrumbs?.items || []);

  // ── Body-class effects ────────────────────────────────────────────────────

  useEffect(() => {
    if (
      data?.showPublicationDate &&
      document?.body?.classList?.contains('contenttype-news-item')
    ) {
      document.body.classList.add('publication-date-visible');
      return () => {
        document.body.classList.remove('publication-date-visible');
      };
    }
  }, [data?.showPublicationDate]);

  // Hide the site breadcrumbs when we're rendering our own inside the hero
  useEffect(() => {
    if (data?.showBreadcrumbs) {
      document.body.classList.add('hero-breadcrumbs-visible');
      return () => {
        document.body.classList.remove('hero-breadcrumbs-visible');
      };
    }
  }, [data?.showBreadcrumbs]);

  // ── Inject anchor IDs onto block-title elements for TOC scrolling ─────────

  useEffect(() => {
    if (data?.useTOC && data?.useBlockTitles) {
      const timeoutId = setTimeout(() => {
        const blocks = properties?.blocks || {};
        const layout = properties?.blocks_layout?.items || [];

        layout.forEach((blockId) => {
          if (blockId === selfBlockId) return;
          const block = blocks[blockId];
          const titleField =
            block?.headline || block?.title || block?.heading || block?.headerText;

          if (titleField && typeof titleField === 'string' && titleField.trim()) {
            const slug = titleField
              .trim()
              .toLowerCase()
              .replace(/<[^>]*>/g, '')
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim();

            if (!document.getElementById(slug)) {
              let blockElement =
                document.querySelector(`[data-rbd-draggable-id="${blockId}"]`) ||
                document.querySelector(`[class*="${blockId}"]`) ||
                document.getElementById(blockId);

              if (!blockElement) {
                const blockType = block['@type'];
                if (blockType) {
                  document
                    .querySelectorAll(`[class*="${blockType}"]`)
                    .forEach((candidate) => {
                      if (candidate.textContent.includes(titleField.trim())) {
                        blockElement = candidate;
                      }
                    });
                }
              }

              if (blockElement) {
                blockElement.id = slug;
                blockElement.setAttribute('data-hero-toc-anchor', 'true');
              }
            }
          }
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [data?.useTOC, data?.useBlockTitles, properties?.blocks, properties?.blocks_layout]);

  // ── Video helpers ─────────────────────────────────────────────────────────

  const handlePlayClick = () => {
    if (videoRef) {
      videoRef.controls = true;
      videoRef.play();
      setVideoPlaying(true);
    }
  };

  const rawId = data?.backgroundVideo?.[0]?.['@id'] || '';
  const filename = rawId.split('/').filter(Boolean).pop();
  const isAlreadyDownloadUrl = rawId.includes('/@@download/');
  const videoUrl =
    rawId && filename
      ? isAlreadyDownloadUrl
        ? rawId
        : `${rawId}/@@download/file/${filename}`
      : null;

  // ── Content resolution ────────────────────────────────────────────────────

  const pageTitle = properties?.title || '';
  const pageDescription = properties?.description || '';
  const displayTitle = data.usePageTitle ? pageTitle : data.title;
  const displaySubtitle = data.usePageDescription ? pageDescription : data.subtitle;

  const publicationDate = data.showPublicationDate ? properties?.effective : null;
  const formattedDate = publicationDate
    ? new Date(publicationDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  // ── Background image URL ──────────────────────────────────────────────────

  const image = data.backgroundImage?.[0];
  const baseUrl = properties?.['@id'] || '';
  const previewImageUrl = `${baseUrl}/@@images/preview_image`;
  const defaultImageUrl = `${baseUrl}/@@images/image`;
  const hasPreviewImage = properties?.preview_image || properties?.has_preview_image;

  let imageUrl = '';
  if (data.usePreviewImage) {
    imageUrl = hasPreviewImage ? previewImageUrl : defaultImageUrl;
  } else if (image) {
    imageUrl = `${image['@id']}/@@images/image`;
  }

  // ── TOC entries ───────────────────────────────────────────────────────────

  const blocks = properties.blocks || {};
  const layout = (properties.blocks_layout?.items) || [];

  const tocEntries = data.useTOC
    ? extractTOCEntries(
        blocks,
        layout,
        data.useH2 ?? true,
        data.useH3 ?? true,
        data.useBlockTitles ?? false,
        selfBlockId,
      )
    : [];

  // ── Breadcrumbs ───────────────────────────────────────────────────────────

  const showBreadcrumbs = data.showBreadcrumbs && breadcrumbItems.length > 0;
  // Prepend Home; the last crumb is the current page — show it as plain text
  const allCrumbs = showBreadcrumbs
    ? [{ url: '/', title: 'Home' }, ...breadcrumbItems]
    : [];

  const heroBreadcrumbs = showBreadcrumbs ? (
    <div className="hero-preheader hero-breadcrumbs">
      {allCrumbs.slice(0, -1).map((crumb, i) => (
        <span key={i}>
          <a href={crumb.url === '/' ? '/' : flattenToAppURL(crumb.url)}>
            {crumb.title}
          </a>
          <span className="hero-breadcrumb-separator" aria-hidden="true"> / </span>
        </span>
      ))}
      <span className="hero-breadcrumb-current">
        {allCrumbs[allCrumbs.length - 1].title}
      </span>
    </div>
  ) : null;

  // ── Video-on-side helpers ─────────────────────────────────────────────────

  const alignment = data.alignment || 'left';
  const showVideoOnSide = data.videoOnSide && videoUrl;
  const videoCTAHref = data.videoCTA?.link ? getHref(data.videoCTA.link) : null;
  const videoPosterImage = data.videoPoster?.[0];
  const videoPosterUrl = videoPosterImage
    ? `${videoPosterImage['@id']}/@@images/image`
    : imageUrl;
  const videoMobileOrder = data.videoMobileOrder || 'top';

  // ── Shared button renderer ────────────────────────────────────────────────

  const renderButtons = () => {
    if (data.useTOC) {
      return tocEntries.map((entry, i) => (
        <a key={i} href={`#${entry.id}`} className="hero-button">
          {entry.title}
        </a>
      ));
    }
    return (data.buttons || []).map((btn, i) => {
      const href = getHref(btn.link);
      const isExternal = (() => {
        try {
          return new URL(href, window.location.origin).hostname !== window.location.hostname;
        } catch {
          return false;
        }
      })();
      return (
        <a
          key={i}
          href={href}
          className="hero-button"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {btn.label || 'Click here'}
        </a>
      );
    });
  };

  const hasButtons = data.useTOC ? tocEntries.length > 0 : data.buttons?.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={[
        'custom-hero-block',
        data.isFullWidth ? 'full-width' : '',
        showVideoOnSide ? 'video-on-side' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Background */}
      {!showVideoOnSide && videoUrl ? (
        <video
          className="hero-video-bg"
          autoPlay
          muted
          loop
          playsInline
          poster={imageUrl}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      ) : (
        <div
          className="hero-image-bg"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
      )}

      <div className="overlay" />

      <div className={`custom-hero-inner align-${alignment}`}>
        {showVideoOnSide ? (
          // ── Video-on-side layout ──────────────────────────────────────────
          <div className="hero-side-by-side">
            <div className="hero-content-column">
              <div className="custom-hero-content">
                {heroBreadcrumbs}
                {data.kicker && <div className="hero-kicker">{data.kicker}</div>}
                {data.showPublicationDate && formattedDate && (
                  <div className="hero-date">{formattedDate}</div>
                )}
                {displayTitle && (
                  <h1
                    dangerouslySetInnerHTML={{
                      __html: displayTitle.replace(/\n/g, '<br />'),
                    }}
                  />
                )}
                {displaySubtitle && (
                  <h3
                    dangerouslySetInnerHTML={{
                      __html: displaySubtitle.replace(/\n/g, '<br />'),
                    }}
                  />
                )}
              </div>
              {hasButtons && (
                <div
                  className={`custom-hero-buttons${data.smallButtons ? ' small-buttons' : ''}`}
                >
                  {renderButtons()}
                </div>
              )}
            </div>

            <div className={`hero-video-column mobile-order-${videoMobileOrder}`}>
              <div className="video-wrapper">
                <video className="hero-side-video" ref={setVideoRef}>
                  <source src={videoUrl} type="video/mp4" />
                </video>
                {!videoPlaying && (
                  <div
                    className="video-overlay"
                    style={{ backgroundImage: `url(${videoPosterUrl})` }}
                  >
                    <button
                      className="video-play-button"
                      onClick={handlePlayClick}
                      aria-label="Play video"
                    >
                      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                        <circle cx="40" cy="40" r="40" fill="rgba(255,255,255,0.9)" />
                        <path d="M32 26L56 40L32 54V26Z" fill="#000" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              {videoCTAHref && data.videoCTA?.label && (
                <div className="video-cta-wrapper">
                  <a
                    href={videoCTAHref}
                    className="hero-button video-cta-button"
                    {...((() => {
                      try {
                        return new URL(videoCTAHref, window.location.origin).hostname !==
                          window.location.hostname
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {};
                      } catch {
                        return {};
                      }
                    })())}
                  >
                    {data.videoCTA.label}
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : (
          // ── Standard layout ───────────────────────────────────────────────
          <>
            <div className="custom-hero-content">
              {heroBreadcrumbs}
              {data.kicker && <div className="hero-kicker">{data.kicker}</div>}
              {data.showPublicationDate && formattedDate && (
                <div className="hero-date">{formattedDate}</div>
              )}
              {displayTitle && (
                <h1
                  dangerouslySetInnerHTML={{
                    __html: displayTitle.replace(/\n/g, '<br />'),
                  }}
                />
              )}
              {displaySubtitle && (
                <h3
                  dangerouslySetInnerHTML={{
                    __html: displaySubtitle.replace(/\n/g, '<br />'),
                  }}
                />
              )}
            </div>
            {hasButtons && (
              <div
                className={`custom-hero-buttons${data.smallButtons ? ' small-buttons' : ''}`}
              >
                {renderButtons()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default View;
